import React from "react";
import "./styles/Button.css";

/**
 * Button - the system's button primitive.
 *
 * Use exactly one `primary` per screen; everything else is `secondary`
 * or `ghost`. `danger` is for the confirming click of a destructive
 * action, `dangerQuiet` for a destructive option sitting among others.
 *
 * @param {Object} props
 * @param {string} props.variant - 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerQuiet' | 'link'
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {React.ReactNode} props.icon - Leading icon
 * @param {React.ReactNode} props.trailingIcon - Trailing icon
 * @param {boolean} props.loading - Swap the leading icon for a spinner and block clicks
 * @param {boolean} props.block - Stretch to the full width of the container
 * @param {string} props.type - Native button type, defaults to "button"
 */
const Button = ({
  variant = "secondary",
  size = "md",
  icon,
  trailingIcon,
  loading = false,
  block = false,
  disabled = false,
  type = "button",
  className = "",
  children,
  ...rest
}) => {
  const iconOnly = !children;

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={[
        "dsButton",
        variant,
        `size-${size}`,
        block ? "isBlock" : "",
        iconOnly ? "isIconOnly" : "",
        loading ? "isLoading" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {loading ? (
        <span className="dsButtonSpinner" aria-hidden="true" />
      ) : (
        icon && <span className="dsButtonIcon">{icon}</span>
      )}
      {children}
      {trailingIcon && !loading && (
        <span className="dsButtonIcon">{trailingIcon}</span>
      )}
    </button>
  );
};

export default Button;
